import { BaseComponent } from '../base-component'
import { getFocusTargets, $el, $list, getTargets } from '../../utils/utils'
import { isTabKey } from '../../utils/key'
import { safePolygon } from './safe-polygon'

export class FloatElementBtn extends BaseComponent {
  element: any
  safePolygonFn: any
  hideTimeoutId: any

  constructor() {
    super()
    this.element = this.getElement()
  }

  mountComponent(): void {
    this.addListener(this.element, 'keydown', this.handleKeyDown)
    this.addListener(this.element, 'blur', this.handleBlur)

    if (this.dataset.trigger === 'click') {
      this.addListener(window, 'click', this.handleWindowClick)
      this.addListener(this.element, 'click', this.handleElementClick)
    } else {
      this.addListener(this.element, 'mouseleave', this.handleMouseLeave)
      this.addListener(this.element, 'mouseenter', this.handleMouseEnter)
    }
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    const { firstTarget } = getTargets(this.floatElement.element)

    if (isTabKey(event) && !event.shiftKey) {
      if (!this.isOpen) {
        event.preventDefault()
        this.floatElement.show()
      }

      if (firstTarget) {
        event.preventDefault()
        this.setTargetListeners('add')
        firstTarget.focus()
      }
    }
  }

  handleBlur = (): void => {
    this.hideTimeoutId = setTimeout(() => {
      this.hideFloatElement()
    }, 0)
  }

  handleFirstTargetFocus = (): void => {}

  handleTargetFocus = (): void => {
    clearTimeout(this.hideTimeoutId)
  }

  handleLastTargetBlur = (): void => {
    this.hideTimeoutId = setTimeout(() => {
      this.hideFloatElement()
    }, 0)
  }

  hideFloatElement() {
    this.setTargetListeners('remove')
    this.floatElement.hide()
  }

  handleMouseEnter = (): void => {
    this.trigger()
  }

  handleElementClick = (): void => {
    this.trigger()
  }

  handleWindowClick = (event: MouseEvent): void => {
    const isCursorOutside = this.floatElement.checkCursorOutside(
      event.clientX,
      event.clientY,
    )
    const isOutsideFloatElements = this.isTargetOutsideFloatElements(
      event.target,
    )

    if (isCursorOutside && isOutsideFloatElements && this.floatElement.isOpen) {
      this.removeListener(window, 'click', this.handleWindowClick)
      this.floatElement.hide()
    }
  }

  handleMouseLeave = (event: MouseEvent): void => {
    if (this.safePolygonFn) {
      this.removeListener(window, 'mousemove', this.handleMouseMove)
    }

    this.safePolygonFn = safePolygon()({
      x: event.clientX,
      y: event.clientY,
      placement: this.floatElement.dataset.placement,
      onClose: () => {
        this.removeListener(window, 'mousemove', this.handleMouseMove)

        if (this.floatElement && this.floatElement.isOpen) {
          this.floatElement.hide()
        }
      },
      elements: { domReference: this.element, floating: this.floatElement },
      nodeId: null,
      tree: null,
    })

    this.safePolygonFn(event)

    this.addListener(window, 'mousemove', this.handleMouseMove)
  }

  handleMouseMove = (event: MouseEvent): void => {
    this.safePolygonFn(event)
  }

  setTargetListeners(action): void {
    const { targets, firstTarget, lastTarget } = getTargets(
      this.floatElement.element,
    )
    const listenerSetter = action === 'add' ? 'addListener' : 'removeListener'

    this[listenerSetter](firstTarget, 'focus', this.handleFirstTargetFocus)
    targets.forEach(target => {
      this[listenerSetter](target, 'focus', this.handleTargetFocus)
    })
    this[listenerSetter](lastTarget, 'blur', this.handleLastTargetBlur)
    this[listenerSetter](window, 'click', this.handleWindowClick)
  }

  getElement() {
    return (
      $el(
        `[data-float-element-trigger="${this.dataset.floatElementId}"]`,
        this,
      ) || this
    )
  }

  isTargetOutsideFloatElements(target) {
    return !this.floatElement.contains(target) && !this.contains(target)
  }

  trigger = (): void => {
    const triggerAction =
      this.dataset.trigger === 'click' ? 'click' : 'mouseenter'

    if (triggerAction === 'click') {
      this.floatElement.isOpen
        ? this.floatElement.hide()
        : this.floatElement.show()

      this.addListener(window, 'click', this.handleWindowClick)
    } else {
      this.floatElement.show()
      this.addListener(this.element, 'mouseleave', this.handleMouseLeave)
    }
  }

  setExpand(isExpanded): void {
    this.setAttribute('data-aria-expanded', isExpanded)
  }

  get floatElement(): any {
    if (!this.dataset.floatElementId) {
      return
    }

    return $el(`#${this.dataset.floatElementId}`)
  }

  get isOpen(): boolean {
    return this.floatElement.isOpen
  }
}
